import { Fragment } from "react";
import {
    Network,
    ParentNetworkMetadata,
    getNetworkTableData,
} from "../../types";
import { NetworkHeader } from "./NetworkHeader";
import {
    Filter,
    FilterMetadata,
    FilterOperators,
    QueryResponse,
} from "~/modules/filters/types";
import { VisualizationGrid } from "~/modules/visualization/components/VisualizationGrid";
import { NetworkTokens } from "./NetworkTokens";
import { ServiceProvider } from "~/modules/service_providers";
import { NetworkServiceProviders } from "./NetworkServiceProviders";
import { Info } from "lucide-react";
import { CANTON_NETWORK_NAME } from "~/consts";
import {
    buildNetworkTimeSeriesModes,
    CantonNetworkTimeSeriesCard,
} from "./NetworkTimeSeriesCard";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import {
    Props as AggregateDisplayProps,
    AggregateGrid,
} from "~/components/AggregateDisplay";
import { HomePageLeagueTable } from "~/modules/main/pages/home/HomePageLeagueTable";
import { LeagueTablePieChart } from "~/modules/league_tables/components/LeagueTablePieChart";
import { VisualizationModeCard } from "~/modules/visualization/components/Timeseries/VisualizationModeCard";

// // // //

const ZKSYNC_NETWORK_SLUG = "zksync-era";

function ZKSyncNetworkDisclaimer(props: { slug: string }) {
    if (ZKSYNC_NETWORK_SLUG !== props.slug) {
        return null;
    }

    return (
        <div className="w-full mt-6">
            <div className="relative overflow-hidden rounded-lg border border-blue-200/50 bg-blue-500/10 p-4 backdrop-blur-md backdrop-filter">
                <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-blue-500/20 blur-xl" />
                <div className="absolute -bottom-10 -left-10 h-20 w-20 rounded-full bg-blue-500/20 blur-xl" />

                <div className="relative flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
                        <Info className="h-4 w-4 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-blue-900">
                        Data available from February 2025 onwards. Historical
                        data backfill coming soon.
                    </p>
                </div>
            </div>
        </div>
    );
}

// // // //

function getTokenTimeseriesFilter(props: {
    network: Network;
    childNetworks: Network[];
}): Filter {
    const { network, childNetworks } = props;

    const filter: Filter =
        childNetworks.length > 0
            ? {
                  operator: FilterOperators.Or,
                  filters: childNetworks.map((childNetwork) => ({
                      field: TokenTimeseriesColumns.networkID,
                      operator: FilterOperators.Equals,
                      value: childNetwork.id,
                  })),
              }
            : {
                  field: TokenTimeseriesColumns.networkID,
                  operator: FilterOperators.Equals,
                  value: network.id,
              };

    return filter;
}

interface Props {
    network: Network;
    aggregates: Array<AggregateDisplayProps>;
    childNetworks: Network[];
    parentNetwork: ParentNetworkMetadata | null;
    tokenFilterMetadata: FilterMetadata[];
    serviceProviders: QueryResponse<ServiceProvider>;
    leagueTableTabs: LeagueTableTabsByDistributionType;
}

export function NetworkIndividualPage(props: Props) {
    const {
        network,
        aggregates,
        tokenFilterMetadata,
        serviceProviders,
        childNetworks,
        parentNetwork,
        leagueTableTabs,
    } = props;

    const networkTableData = getNetworkTableData([network, ...childNetworks]);

    // Define flags to check if the network (or child networks) has RWA or Stablecoin assets
    const showRWAVisualizations = leagueTableTabs.distributed.some(
        (t) => t.data.rows.length > 0,
    );

    const showRepresentedVisualizations = leagueTableTabs.represented.some(
        (t) => t.data.rows.length > 0,
    );

    const showStablecoinVisualizations = networkTableData.some(
        (n) => !!n.stablecoin_asset_count,
    );

    const onlyHasStablecoins =
        showStablecoinVisualizations &&
        !showRepresentedVisualizations &&
        !showRWAVisualizations;

    // Define flag to check if the network is Canton (swaps out visualizations for Canton-specific timeseries)
    const isCanton = network.name === CANTON_NETWORK_NAME;

    // Define a flag to check if the network has child networks
    const hasChildNetworks = childNetworks.length > 0;

    // Define title for League Table
    const leagueTableTitle = hasChildNetworks
        ? `${network.name} (All Chains) RWA League Table`
        : `${network.name} RWA League Table`;

    // Define title for Timeseries
    const timeseriesTitle = hasChildNetworks
        ? `${network.name} (All Chains) Metrics`
        : `${network.name} Metrics`;

    return (
        <Fragment>
            <NetworkHeader
                network={network}
                hasChildNetworks={hasChildNetworks}
                parentNetwork={parentNetwork}
            />

            <AggregateGrid aggregates={aggregates} />

            <ZKSyncNetworkDisclaimer slug={network.slug} />

            <VisualizationGrid className="mt-6">
                {isCanton ? (
                    <CantonNetworkTimeSeriesCard
                        title={`${network.name} Metrics`}
                        defaultGroupBy={TokenTimeseriesGroupBy.asset}
                        enableGroupByControls={false}
                        columnSpanConfig={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                            xxl: 12,
                        }}
                        filters={[
                            {
                                field: TokenTimeseriesColumns.networkID,
                                operator: FilterOperators.Equals,
                                value: network.id,
                            },
                        ]}
                    />
                ) : (
                    <VisualizationModeCard
                        title={timeseriesTitle}
                        distributionSelect={{
                            distributed: {
                                enabled: true,
                            },
                            represented: {
                                enabled: true,
                            },
                            stablecoins: {
                                enabled: true,
                                active: false,
                            },
                        }}
                        modes={[
                            ...buildNetworkTimeSeriesModes({
                                network,
                                filters: [
                                    getTokenTimeseriesFilter({
                                        network,
                                        childNetworks,
                                    }),
                                ],
                                defaultGroupBy: TokenTimeseriesGroupBy.asset,
                                enableGroupByControls: true,
                            }),
                        ]}
                        columnSpanConfig={{
                            sm: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                            xxl: 12,
                        }}
                    />
                )}

                {/* <div className="col-span-12 xl:col-span-6 2xl:col-span-6">
                    <TableCard title="Key Facts">
                        <FactsTable
                            row={network}
                            columns={[
                                NAME_INDIVIDUAL_COLUMN,
                                ASSET_COUNT_COLUMN,
                                MARKET_CAP_COLUMN,
                                TRANSFER_VOLUME_COLUMN,
                                HOLDERS_COUNT_COLUMN,
                                ACTIVE_ADDRESSES_COUNT_COLUMN,
                                NEW_ISSUANCE_COLUMN,
                            ]}
                        />
                    </TableCard>
                </div> */}

                <HomePageLeagueTable
                    title={leagueTableTitle}
                    analyticsTableName="network-league-table"
                    leagueTableTabs={leagueTableTabs}
                    filterEmptyTabs={false}
                />

                <LeagueTablePieChart
                    title="Total RWA Value"
                    leagueTableTabs={leagueTableTabs}
                    defaultGroupBy={LeagueTableGroupTypes.platforms}
                    allowedGroupByValues={[
                        LeagueTableGroupTypes.platforms,
                        LeagueTableGroupTypes.assetClasses,
                        LeagueTableGroupTypes.managers,
                    ]}
                />
            </VisualizationGrid>

            {/* Hide NetworkTokens for Canton network */}
            {!isCanton && (
                <NetworkTokens
                    network={network}
                    childNetworks={childNetworks}
                    filterMetadata={tokenFilterMetadata}
                />
            )}

            <NetworkServiceProviders serviceProviders={serviceProviders} />
        </Fragment>
    );
}
